/**
 * Gateways - SIP Gateway/Trunk Management
 * Create, edit, and manage SIP gateways
 */

import React, { useState, useEffect } from 'react';
import { 
  Network, Plus, Edit2, Trash2, Search,
  Loader2, ToggleLeft, ToggleRight, Server,
  Phone, Shield, CheckCircle, XCircle, X,
  ChevronDown, ChevronUp, AlertCircle, Copy, Eye, EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Type badge styles
const TYPE_STYLES = {
  peer: { color: 'bg-blue-100 text-blue-800', label: 'Peer' },
  friend: { color: 'bg-purple-100 text-purple-800', label: 'Friend' },
  user: { color: 'bg-gray-100 text-gray-800', label: 'User' }
};

// Default form values
const DEFAULT_FORM = {
  name: '',
  host: '',
  port: '5060',
  username: '',
  secret: '',
  type: 'peer',
  transport: 'udp',
  nat: 'force_rport,comedia',
  dtmfmode: 'rfc2833',
  insecure: 'port,invite',
  canreinvite: 'no',
  outboundproxy: '',
  fromdomain: '',
  sendrpid: 'no',
  trustrpid: 'no',
  call_limit: 0,
  register_trunk: false,
  context: 'from-trunk',
  description: '',
  status: true
};

export default function Gateways() {
  const { user: currentUser } = useAuth();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [deletingGateway, setDeletingGateway] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  useEffect(() => {
    loadGateways();
  }, []);

  const loadGateways = async () => {
    try {
      setLoading(true);
      const response = await api.getGateways();
      setGateways(response.data.data?.gateways || []);
    } catch (error) {
      console.error('Failed to load gateways:', error);
      toast.error('Failed to load gateways');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingGateway(null);
    setFormData(DEFAULT_FORM);
    setShowAdvanced(false);
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (gateway) => {
    setEditingGateway(gateway);
    setFormData({
      name: gateway.name || '',
      host: gateway.host || '',
      port: gateway.port || '5060',
      username: gateway.username || '',
      secret: gateway.secret || '',
      type: gateway.type || 'peer',
      transport: gateway.transport || 'udp',
      nat: gateway.nat || 'force_rport,comedia',
      dtmfmode: gateway.dtmfmode || 'rfc2833',
      insecure: gateway.insecure || 'port,invite',
      canreinvite: gateway.canreinvite || 'no',
      outboundproxy: gateway.outboundproxy || '',
      fromdomain: gateway.fromdomain || '',
      sendrpid: gateway.sendrpid || 'no',
      trustrpid: gateway.trustrpid || 'no',
      call_limit: gateway.call_limit || 0,
      register_trunk: gateway.is_registered || false,
      context: gateway.context || 'from-trunk',
      description: gateway.description || '',
      status: gateway.status === 1 || gateway.status === true
    });
    setShowAdvanced(false);
    setShowPassword(false);
    setShowModal(true);
  };

  const openDeleteModal = (gateway) => {
    setDeletingGateway(gateway);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.host) {
      toast.error('Name and Host are required');
      return;
    }

    try {
      setSaving(true);
      
      if (editingGateway) {
        await api.updateGateway(editingGateway.id, formData);
        toast.success('Gateway updated successfully');
      } else {
        await api.createGateway(formData);
        toast.success('Gateway created successfully');
      }
      
      setShowModal(false);
      loadGateways();
    } catch (error) {
      const message = error.response?.data?.error?.message || 
                      error.response?.data?.error || 
                      'Failed to save gateway';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingGateway) return;

    try {
      setSaving(true);
      await api.deleteGateway(deletingGateway.id);
      toast.success('Gateway deleted successfully');
      setShowDeleteModal(false);
      setDeletingGateway(null);
      loadGateways();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete gateway');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (gateway) => {
    try {
      await api.toggleGatewayStatus(gateway.id);
      toast.success(`Gateway ${gateway.status ? 'disabled' : 'enabled'} successfully`);
      loadGateways();
    } catch (error) {
      toast.error('Failed to update gateway status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filteredGateways = gateways.filter(gateway => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      gateway.name?.toLowerCase().includes(searchLower) ||
      gateway.host?.toLowerCase().includes(searchLower) ||
      gateway.description?.toLowerCase().includes(searchLower)
    );
  });

  const canManageGateways = ['admin', 'super_admin'].includes(currentUser?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SIP Gateways</h1>
          <p className="text-gray-500">Manage SIP trunks and gateway connections</p>
        </div>
        {canManageGateways && (
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Gateway
          </button>
        )}
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search gateways by name, host, or description..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Server className="w-4 h-4" />
            <span>{filteredGateways.length} gateway{filteredGateways.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Gateways Grid */}
      {filteredGateways.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Network className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No gateways found</h3>
          <p className="text-gray-500 mb-6">
            {search ? 'Try adjusting your search terms' : 'Add your first SIP gateway to get started'}
          </p>
          {canManageGateways && !search && (
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Gateway
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredGateways.map((gateway) => {
            const typeStyle = TYPE_STYLES[gateway.type] || TYPE_STYLES.peer;
            
            return (
              <div 
                key={gateway.id} 
                className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow ${
                  !gateway.status ? 'opacity-60' : ''
                }`}
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${gateway.status ? 'bg-primary-100' : 'bg-gray-100'}`}>
                        <Network className={`w-5 h-5 ${gateway.status ? 'text-primary-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{gateway.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeStyle.color}`}>
                            {typeStyle.label}
                          </span>
                          {gateway.status ? (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <XCircle className="w-3 h-3" />
                              Disabled
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {canManageGateways && (
                      <button
                        onClick={() => handleToggleStatus(gateway)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={gateway.status ? 'Disable gateway' : 'Enable gateway'}
                      >
                        {gateway.status ? (
                          <ToggleRight className="w-6 h-6 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-gray-400" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Card Body */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Host</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-gray-900">{gateway.host}:{gateway.port || '5060'}</span>
                      <button 
                        onClick={() => copyToClipboard(`${gateway.host}:${gateway.port || '5060'}`)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                  
                  {gateway.username && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Username</span>
                      <span className="font-mono text-gray-900">{gateway.username}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Transport</span>
                    <span className="text-gray-900 uppercase">{gateway.transport || 'UDP'}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Call Limit</span>
                    <span className="text-gray-900">{gateway.call_limit || 'Unlimited'}</span>
                  </div>
                  
                  {gateway.is_registered && (
                    <div className="flex items-center gap-2 text-sm">
                      <Shield className="w-4 h-4 text-green-500" />
                      <span className="text-green-600">Registration Enabled</span>
                    </div>
                  )}
                  
                  {gateway.description && (
                    <p className="text-sm text-gray-500 pt-2 border-t border-gray-100">
                      {gateway.description}
                    </p>
                  )}
                </div>
                
                {/* Card Actions */}
                {canManageGateways && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
                    <button
                      onClick={() => openEditModal(gateway)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => openDeleteModal(gateway)}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingGateway ? 'Edit Gateway' : 'Add Gateway'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-6">
                {/* Basic Settings */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gateway Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., trunk-provider1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="peer">Peer</option>
                        <option value="friend">Friend</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Host / IP Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.host}
                        onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                        placeholder="e.g., sip.provider.com or 192.168.1.100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port
                      </label>
                      <input
                        type="text"
                        value={formData.port}
                        onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                        placeholder="5060"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Authentication */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Authentication</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        placeholder="SIP username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password / Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.secret}
                          onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
                          placeholder="SIP password"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.register_trunk}
                        onChange={(e) => setFormData({ ...formData, register_trunk: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                    <span className="text-sm text-gray-700">Register this trunk with the provider</span>
                  </div>
                </div>
                
                {/* SIP Settings */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">SIP Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Transport
                      </label>
                      <input
                        type="text"
                        value={formData.transport}
                        onChange={(e) => setFormData({ ...formData, transport: e.target.value })}
                        placeholder="udp"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        NAT
                      </label>
                      <input
                        type="text"
                        value={formData.nat}
                        onChange={(e) => setFormData({ ...formData, nat: e.target.value })}
                        placeholder="force_rport,comedia"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        DTMF Mode
                      </label>
                      <select
                        value={formData.dtmfmode}
                        onChange={(e) => setFormData({ ...formData, dtmfmode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="rfc2833">RFC2833</option>
                        <option value="inband">Inband</option>
                        <option value="info">INFO</option>
                        <option value="auto">Auto</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Call Limit
                      </label>
                      <select
                        value={formData.call_limit}
                        onChange={(e) => setFormData({ ...formData, call_limit: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="0">Unlimited</option>
                        {[...Array(100)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Can Reinvite
                      </label>
                      <select
                        value={formData.canreinvite}
                        onChange={(e) => setFormData({ ...formData, canreinvite: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Insecure
                      </label>
                      <input
                        type="text"
                        value={formData.insecure}
                        onChange={(e) => setFormData({ ...formData, insecure: e.target.value })}
                        placeholder="port,invite"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Advanced Settings Toggle */}
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
                </button>
                
                {/* Advanced Settings */}
                {showAdvanced && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Outbound Proxy
                        </label>
                        <input
                          type="text"
                          value={formData.outboundproxy}
                          onChange={(e) => setFormData({ ...formData, outboundproxy: e.target.value })}
                          placeholder="e.g., sip:proxy.provider.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          From Domain
                        </label>
                        <input
                          type="text"
                          value={formData.fromdomain}
                          onChange={(e) => setFormData({ ...formData, fromdomain: e.target.value })}
                          placeholder="e.g., yourdomain.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Send RPID
                        </label>
                        <select
                          value={formData.sendrpid}
                          onChange={(e) => setFormData({ ...formData, sendrpid: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="pai">PAI (P-Asserted-Identity)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Trust RPID
                        </label>
                        <select
                          value={formData.trustrpid}
                          onChange={(e) => setFormData({ ...formData, trustrpid: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Context
                        </label>
                        <input
                          type="text"
                          value={formData.context}
                          onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                          placeholder="from-trunk"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      
                      <div className="flex items-center gap-3 pt-6">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                        <span className="text-sm text-gray-700">Gateway Enabled</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Optional description for this gateway"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingGateway ? 'Update Gateway' : 'Create Gateway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingGateway && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Gateway</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <strong>{deletingGateway.name}</strong>? 
                All calls using this gateway will fail.
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingGateway(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Delete Gateway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}